<template>
    <Modal size="md" allow-overflow @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">
                Export {{ templateCount }}
                {{ templateCount === 1 ? 'template' : 'templates' }} to Lettr
            </div>
        </template>

        <template #body>
            <div class="overflow-visible p-6">
                <form>
                    <SelectInput
                        v-if="integrations.length > 1"
                        v-model="state.integration_id"
                        class="mb-6"
                        placeholder="Choose integration"
                        :data="integrationOptions"
                        label="Lettr account"
                        required
                        :disabled="loading"
                    />

                    <SelectInput
                        v-model="state.purpose"
                        placeholder="Choose purpose"
                        :data="purposes"
                        label="Purpose"
                        required
                        :disabled="loading"
                    />

                    <SelectInput
                        v-model="folderId"
                        class="mt-6"
                        :placeholder="folderPlaceholder"
                        :data="folders"
                        label="Folder"
                        :error="foldersError"
                        :disabled="loading || foldersLoading || folders.length === 0"
                    />

                    <p
                        v-if="overCap"
                        class="mt-4 rounded-[4px] bg-yellow-100 px-[14px] py-3 text-sm text-yellow-800"
                    >
                        {{ templateCount }} templates selected, and at most
                        {{ MAX_TEMPLATES }} can go at once. Deselect
                        {{ templateCount - MAX_TEMPLATES }} and try again.
                    </p>

                    <p v-else class="mt-2 text-sm text-gray-500">
                        All {{ templateCount }} templates land in the same
                        folder. Lettr keeps campaign and transactional templates
                        apart, each with its own folders.
                    </p>
                </form>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || foldersLoading || overCap || !state.integration_id"
                    @click="exportTemplates"
                >
                    Export
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, computed, reactive, ref, toRef } from 'vue';
import { IIntegration, ILettr } from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import ISelectable from '@/types/Atomic/ISelectable';
import { create } from '@composables/useNotifications';
import useLettrFolders from '../composables/useLettrFolders';
import useLettrExportProgress from '../composables/useLettrExportProgress';

/** Mirrors ExportMultipleTemplatesToLettrRequest::MAX_TEMPLATES. */
const MAX_TEMPLATES = 100;

const emit = defineEmits(['close', 'started']);

const props = defineProps({
    integrations: {
        required: true,
        type: Array as PropType<IIntegration<ILettr>[]>,
    },
    templateIds: {
        required: true,
        type: Array as PropType<number[]>,
    },
});

const templateCount = computed(() => props.templateIds.length);
const { finish: finishProgress } = useLettrExportProgress();
const overCap = computed(() => templateCount.value > MAX_TEMPLATES);

const state = reactive({
    integration_id: props.integrations[0]?.id ?? null,
    purpose: 'campaign',
});

const loading = ref(false);

const integrationOptions = computed<ISelectable[]>(() =>
    props.integrations.map((integration) => ({
        title: integration.name || 'Lettr',
        value: integration.id,
    }))
);

const purposes = ref<ISelectable[]>([
    { title: 'Campaign', value: 'campaign' },
    { title: 'Transactional', value: 'transactional' },
]);

const {
    folders,
    folderId,
    loading: foldersLoading,
    error: foldersError,
    load: loadFolders,
} = useLettrFolders(
    toRef(state, 'integration_id'),
    toRef(state, 'purpose')
);

loadFolders();

const folderPlaceholder = computed(() => {
    if (foldersLoading.value) {
        return 'Loading folders…';
    }

    return folders.value.length === 0 ? 'No folders in Lettr' : 'Choose folder';
});

const exportTemplates = async () => {
    loading.value = true;

    // Both before the request, not after it. The run is queued work reported
    // over its own channel, so the modal's job ends once the request is sent -
    // and on the sync queue driver the whole run happens inside this call, so
    // waiting for it would hold the modal open for the entire export and let
    // every progress event pass before the bar existed.
    emit('started');
    emit('close');

    try {
        await axios.post(route('templates.multiple.exports.lettr'), {
            ids: props.templateIds,
            integration_id: state.integration_id,
            purpose: state.purpose,
            folder_id: folderId.value === '' ? null : folderId.value,
        });
    } catch (e) {
        // Nothing was queued, so the selection stays put for another go.
        finishProgress();
        create({
            title: 'Export not started',
            text:
                axios.isAxiosError(e) && e.response?.data?.message
                    ? e.response.data.message
                    : 'The export could not be started. Please try again.',
            type: 'error',
        });
    } finally {
        loading.value = false;
    }
};
</script>
